import { defineStore } from 'pinia';
import { computed, type ComputedRef, type Ref, ref } from 'vue';
import * as signalR from '@microsoft/signalr';
import { type HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
// Type-only imports (erased at runtime, so they add no static import cycle) used to type the
// lazily resolved feature stores passed into the extracted registerEventHandlers helper.
import type { useImportWizardStore as UseImportWizardStoreFn } from '@/store/importWizardStore';
import type { useUploadStore as UseUploadStoreFn } from '@/store/storeUpload';
import type { useTableStore as UseTableStoreFn } from '@/store/storeTable';

// TypeScript interfaces basert på C#-klassene
// NB: Enum-verdier MÅ matche C# (PascalCase) - JSON konverterer kun keys, ikke values
export enum ProcessType {
  upload = 'Upload',
  validation = 'Validation',
  import = 'Import',
  export = 'Export',
  analyse = 'Analyse',
  // US 3114 task 3118: module provisioning (e.g. PowerBI/Fabric) emits step progress as Setup events.
  setup = 'Setup',
  /** Brukes kun i FE for visning når processType er Import og processStatus er AwaitingUserDecision. Backend sender aldri denne. */
  simulering = 'Simulering'
}

export enum ProcessStatus {
  pending = 'Pending',
  uploading = 'Uploading',
  processing = 'Processing',
  completed = 'Completed',
  failed = 'Failed',
  ready = 'Ready',
  timeout = 'Timeout',
  unknown = 'Unknown',
  cancelled = 'Cancelled',
  awaitingUserDecision = 'AwaitingUserDecision',
  aborted = 'Aborted'
}

/** Backend sender kun Import, ikke Simulering. «Simulering»-fasen er Import + AwaitingUserDecision. */
export function isSimuleringPhase (processType: ProcessType, processStatus?: ProcessStatus): boolean {
  return processType === ProcessType.import && processStatus === ProcessStatus.awaitingUserDecision;
}

export interface ProgressResponse {
  uploadId: string;
  dbName: string;
  processType: ProcessType;
  processStatus: ProcessStatus;
  isComplete: boolean;
  hasError: boolean;
  progressPercent?: number;
  message?: string;
  timestamp?: string;
  taskName: string;
  importId: string;
  fileName?: string;
}

export interface IcsBlobItemResponse {
  uploadId: string;
  dbName: string;
  processType: ProcessType;
  processStatus: ProcessStatus;
  isComplete: boolean;
  isProcessing: boolean;
  isFailed: boolean;
  hasError: boolean;
  progressPercent?: number;
  message?: string;
  timestamp?: string;
  extractedFromUploadId?: string;
  fileSize: number;
  contentType: string;
  fileName: string;
  importDefinitionKey?: string;
  importDefinitions?: any[];
  isReadyForImport?: boolean;
  isTextFile?: boolean;
  fileMetaData?: any;
  sheets?: Record<string, string>;
  extractedFiles?: Record<string, string>;
  uploadedDate: string;
  processedDate?: string;
  sheetName?: string;
  isExcel?: boolean;
  isExcelSheet?: boolean;
  isImported?: boolean;
  hasImportError?: boolean;
  uploadedBy?: string;
  lastImportStartedBy?: string;
  isArchived: boolean;
  lastImport?: string;
  error?: string;
  counterData?: CounterData[] | null;
  canImportFile?: boolean;
  isInBlobStorage?: boolean;
  hasImportLog?: boolean;
  importLogComplete?: boolean;
  importLogHasError?: boolean;
  importId?: string;
}

export interface ExportTaskStatus {
  exportId: string;
  dbName: string;
  processType: ProcessType;
  processStatus: ProcessStatus;
  isComplete: boolean;
  hasError: boolean;
  progressPercent?: number;
  message?: string;
  timestamp?: string;
  status: string;
  rowsExported: number;
  name: string;
  contentType: string;
}

export interface CounterData {
  name: string;
  count: number;
  description?: string;
}

/**
 * Task 2584 (US 2524): duplikat-konteksten for en import som venter på kjøringsvalg —
 * antall duplikatgrupper og antall rader i gruppene. Speiler BE-kontrakten
 * `ImportDuplicateAwaitContext` fra process-status-payloaden (task 2581). Null/utelatt
 * når pausen ikke skyldes duplikater.
 */
export interface ImportDuplicateAwaitContext {
  groupCount: number;
  rowCount: number;
}

export interface EventStreamUpdate {
  dbName: string;
  fileId: string;
  jobId: string;
  processType: ProcessType;
  processStatus: ProcessStatus;
  /**
   * Bug #2064: STEGETS eget utfall (Completed/Failed) der processStatus beskriver
   * KJØRINGEN — «Steg fullført»-eventer har processStatus=Processing. null/utelatt =
   * ikke steg-terminalt event; konsumenter faller tilbake til run-status-utledning.
   */
  stepStatus?: ProcessStatus | null;
  message?: string;
  timestamp: string;
  progressPercent?: number;
  hasError: boolean;
  /** Steg-identitet for gruppering (f.eks. aktivitetsnavn som CountRowsBefore_Activity). */
  taskId?: string;
  taskName?: string;
  fileName?: string;
  isComplete: boolean;
  counterData?: CounterData[] | null;
  /**
   * Task 2584 (US 2524): duplikat-konteksten når prosessen venter på duplikat-valg.
   * SignalR-eventet fra BE bærer den IKKE — feltet settes klientside fra
   * process-status-payloaden (aggregat-poll / henting ved venter-på-bruker) og er
   * null/utelatt ellers. Venter-flatene bruker den til å skille duplikat-pausen fra
   * validerings-gaten.
   */
  duplicateAwait?: ImportDuplicateAwaitContext | null;
}

export interface AnalyseProgressUpdate {
  jobId: string;
  uploadId: string;
  dbName: string;
  status: string;
  progressPercent?: number | null;
  message?: string | null;
  result?: any;
  hasError: boolean;
  errorMessage?: string | null;
}

export interface NotificationEvent {
  eventId?: string;
  severity: 'error' | 'success' | 'secondary' | 'info' | 'warn';
  summary: string;
  detail?: string;
}

export interface Notification extends NotificationEvent {
  id: number;
  createdAt: string;
  isActive: boolean;
  color: string;
  icon: string;
}

export interface Problem {
  title: string;
  detail?: string;
  errors?: Record<string, string[]> | string[];
}

/**
 * Bug 2970: utfallet av en asynkront sendt varslings-e-post, pushet fra workeren over
 * SignalR til den handlende brukerens `user_{oid}`-gruppe. Speiler BE-kontrakten
 * `EmailOutcomeUpdate` (camelCase JSON; enum-verdiene er PascalCase som i C#).
 */
export type EmailNotificationOutcome = 'Sent' | 'Failed' | 'Partial' | 'Skipped';

export type NotificationEmailKind =
  | 'AccessRequestCreatedToAdmins'
  | 'AccessRequestApproved'
  | 'AccessRequestRejected'
  | 'ModuleActivationRequestCreatedToAdmins'
  | 'ModuleActivationRequestProcessed';

export interface EmailOutcomeUpdate {
  actorOid: string;
  kind: NotificationEmailKind;
  outcome: EmailNotificationOutcome;
  failedRecipients: string[];
  dbName: string | null;
  requestId: string | null;
}

// Hjelpefunksjoner for å normalisere processType og processStatus fra backend
export function normalizeProcessType (value: string | ProcessType | undefined): ProcessType {
  // Håndter undefined/null
  if (value === undefined || value === null) {
    console.warn('[NotificationsStore] processType er undefined/null, bruker default:', ProcessType.upload);
    return ProcessType.upload;
  }

  // Hvis det allerede er en ProcessType enum-verdi, returner den
  if (typeof value !== 'string') {
    return value as ProcessType;
  }

  // Håndter string-verdier med case-insensitive matching
  const enumValues = Object.values(ProcessType) as string[];
  const matchedValue = enumValues.find(v => v.toLowerCase() === value.toLowerCase());
  if (matchedValue) {
    return matchedValue as ProcessType;
  }

  console.warn(`[NotificationsStore] Ukjent processType: ${value}, bruker default: ${ProcessType.upload}`);
  return ProcessType.upload;
}

function normalizeProcessStatus (value: string | ProcessStatus | undefined): ProcessStatus {
  // Håndter undefined/null
  if (value === undefined || value === null) {
    console.warn('[NotificationsStore] processStatus er undefined/null, bruker default:', ProcessStatus.unknown);
    return ProcessStatus.unknown;
  }

  // Hvis det allerede er en ProcessStatus enum-verdi, returner den
  if (typeof value !== 'string') {
    return value as ProcessStatus;
  }

  // Håndter string-verdier med case-insensitive matching
  const enumValues = Object.values(ProcessStatus) as string[];
  const matchedValue = enumValues.find(v => v.toLowerCase() === value.toLowerCase());
  if (matchedValue) {
    return matchedValue as ProcessStatus;
  }

  console.warn(`[NotificationsStore] Ukjent processStatus: ${value}, bruker default: ${ProcessStatus.unknown}`);
  return ProcessStatus.unknown;
}

// Builds the SignalR hub connection for a scope. The access token is fetched lazily to
// avoid a static import cycle against the auth helpers. Behavior is identical to the
// inline builder it was extracted from.
function buildConnection (connectionUrl: string): HubConnection {
  return new HubConnectionBuilder()
    .withUrl(connectionUrl, {
      accessTokenFactory: async () => {
        const { getShellAccessTokenSilent } = await import('@/helpers/auth/useAuthShell');
        const tokenResponse = await getShellAccessTokenSilent();
        if (!tokenResponse?.accessToken) {
          throw new Error('Kunne ikke hente autentiseringstoken for SignalR-tilkobling');
        }
        return tokenResponse.accessToken;
      }
    })
    // Warning som standard: Information logger hele WS-URL-en inkl. access_token til console (WI 2087)
    .configureLogging(signalR.LogLevel.Warning)
    .withAutomaticReconnect()
    .build();
}

export const useNotificationsStore = defineStore('storeNotifications', () => {
  let connection: HubConnection | null = null;
  let isConnecting = false;
  const currentDatabase: Ref<string | null> = ref(null);
  const notifications: Ref<Notification[]> = ref([]);
  const showNotifications: Ref<boolean> = ref(false);
  const progresses: Ref<Record<string, EventStreamUpdate>> = ref({});

  const getProgress = (id: string): ComputedRef<EventStreamUpdate | null> => {
    return computed(() => {
      if (!id) return null;
      // Prøv først med id direkte (kan være fileId)
      if (progresses.value[id]) {
        return progresses.value[id];
      }
      // Hvis ikke funnet, kan det være uploadId - søk gjennom alle entries
      const found = Object.values(progresses.value).find(p => p.fileId === id);
      return found || null;
    });
  };

  const setProgress = (update: EventStreamUpdate): void => {
    progresses.value = {
      ...progresses.value,
      [update.fileId]: update,
    };
  };


  function toggleNotifications (): void {
    showNotifications.value = !showNotifications.value;
  }

  // Bug 2970: menneskelesbar etikett per e-post-`kind` for utfalls-toasten.
  const EMAIL_KIND_LABEL: Record<NotificationEmailKind, string> = {
    AccessRequestCreatedToAdmins: 'ny tilgangsforespørsel',
    AccessRequestApproved: 'godkjent tilgangsforespørsel',
    AccessRequestRejected: 'avslått tilgangsforespørsel',
    ModuleActivationRequestCreatedToAdmins: 'ny modulforespørsel',
    ModuleActivationRequestProcessed: 'behandlet modulforespørsel'
  };

  /**
   * Bug 2970: ruter det asynkrone e-postutfallet til en ærlig toast. BE eier logikken
   * (send/map i workeren); her velges kun visningen. `Failed`/`Partial` navngir de feilede
   * mottakerne — «Send e-post på nytt» (resend-handlingene på storene) er neste steg.
   * `Skipped` (SendGrid ukonfigurert) gir en nøytral bekreftelse UTEN «varslet»-påstand.
   */
  function handleEmailOutcome (update: EmailOutcomeUpdate): void {
    const label = EMAIL_KIND_LABEL[update.kind] ?? 'varsling';
    const recipients = (update.failedRecipients ?? []).filter(Boolean);
    const recipientList = recipients.length > 0 ? recipients.join(', ') : 'mottaker(e)';

    switch (update.outcome) {
      case 'Sent':
        addSuccess('Varsel sendt', `Varsel for ${label} er sendt på e-post.`);
        break;
      case 'Failed':
        addError('Varsling feilet', `Klarte ikke sende e-post til ${recipientList}. Bruk «Send e-post på nytt».`);
        break;
      case 'Partial':
        addError('Varsling delvis feilet', `Klarte ikke sende e-post til ${recipientList}. Bruk «Send e-post på nytt».`);
        break;
      case 'Skipped':
        addSuccess('Handling fullført', 'E-postvarsling er ikke aktivert i dette miljøet; ingen e-post ble sendt.');
        break;
      default:
        console.warn('[NotificationsStore] Ukjent e-postutfall:', update.outcome);
        break;
    }
  }

  // Bug 2970: the guard chain for initializeConnection. Returns true when the connection
  // attempt must be skipped (nothing to connect to, an init already running, or a live
  // connection already covers this scope). Logging side effects are preserved verbatim.
  const shouldSkipInitialization = (dbName: string, oid: string): boolean => {
    // Bug 2970: en bruker-scopet kobling (uten dbName) er gyldig så lenge vi har en
    // bruker-id — pushen til user_{oid} (bl.a. e-postutfallet) trenger den gruppen.
    // Genuint ingenting å koble til = verken database eller bruker.
    if (!dbName && !oid) {
      console.log('[SignalR] Verken database eller bruker-id — kobler ikke til SignalR');
      return true;
    }

    // Guard: Forhindre dobbel initialisering
    if (isConnecting) {
      console.log('[SignalR] SignalR-tilkobling allerede i gang, venter...');
      return true;
    }

    // Hvis vi allerede er tilkoblet til samme scope, ikke gjør noe
    if (connection && currentDatabase.value === dbName) {
      const state = connection.state;
      console.log('[SignalR] Eksisterende tilkobling funnet, state:', state, 'scope:', currentDatabase.value);
      if (state === signalR.HubConnectionState.Connected || state === signalR.HubConnectionState.Connecting) {
        console.log('[SignalR] SignalR allerede tilkoblet til scope:', dbName || '(bruker)');
        return true;
      }
    }

    // Bug 2970: en bruker-scopet forespørsel skal ALDRI rive ned en levende prosjekt-
    // kobling — den koblingen joiner user_{oid} på BE-siden også, så bruker-gruppen er
    // allerede dekket. Uten dette ville en re-fyrt login/bootstrap-init nedgradere en
    // åpen prosjekt-kobling og miste prosjekt-gruppen.
    if (!dbName && connection) {
      const state = connection.state;
      if (state === signalR.HubConnectionState.Connected || state === signalR.HubConnectionState.Connecting) {
        console.log('[SignalR] Bruker-scopet init hoppet over — levende kobling dekker allerede bruker-gruppen');
        return true;
      }
    }

    return false;
  };

  // Registers every SignalR hub handler on a freshly started connection. Extracted from
  // initializeConnection verbatim so the orchestration stays readable. The feature stores
  // are passed in because they are resolved lazily inside initializeConnection.
  const registerEventHandlers = (
    conn: HubConnection,
    importWizardStore: ReturnType<typeof UseImportWizardStoreFn>,
    uploadStore: ReturnType<typeof UseUploadStoreFn>,
    tableStore: ReturnType<typeof UseTableStoreFn>
  ): void => {
    // Register event listeners
    conn.on('ReceiveProgress', (progressResponse: any) => {
      console.log('ReceiveProgress', progressResponse);
      // Normaliser processType og processStatus
      const normalizedProgress: ProgressResponse = {
        ...progressResponse,
        processType: normalizeProcessType(progressResponse.processType),
        processStatus: normalizeProcessStatus(progressResponse.processStatus)
      };

      // Konverter ProgressResponse til EventStreamUpdate
      const eventStreamUpdate: EventStreamUpdate = {
        dbName: normalizedProgress.dbName,
        fileId: normalizedProgress.uploadId, // ProgressResponse bruker uploadId, EventStreamUpdate bruker fileId
        jobId: normalizedProgress.importId, // ProgressResponse bruker importId, EventStreamUpdate bruker jobId
        processType: normalizedProgress.processType,
        processStatus: normalizedProgress.processStatus,
        message: normalizedProgress.message,
        timestamp: normalizedProgress.timestamp || new Date().toISOString(),
        progressPercent: normalizedProgress.progressPercent,
        hasError: normalizedProgress.hasError,
        taskName: normalizedProgress.taskName,
        fileName: normalizedProgress.fileName,
        isComplete: normalizedProgress.isComplete,
        counterData: null
      };

      setProgress(eventStreamUpdate);

      // Håndter import (inkl. stopp-for-bruker = simulering) via importWizardStore. Backend sender kun Import.
      if (normalizedProgress.processType === ProcessType.import) {
        importWizardStore.handleProgressUpdate(normalizedProgress).catch((err: unknown) => {
          console.error('Feil ved håndtering av progress update:', err);
        });
      }
    });

    conn.on('ReceiveUploadProgress', (progressResponse: any) => {
      console.log('ReceiveUploadProgress', progressResponse);
      // Normaliser processType og processStatus
      const normalizedProgress: IcsBlobItemResponse = {
        ...progressResponse,
        processType: normalizeProcessType(progressResponse.processType),
        processStatus: normalizeProcessStatus(progressResponse.processStatus)
      };
      uploadStore.updateOrAddFile(normalizedProgress);

      // Håndter wizard state hvis filen er ferdig eller har feilet
      if (normalizedProgress.uploadId &&
        (normalizedProgress.processStatus === ProcessStatus.ready ||
          normalizedProgress.processStatus === ProcessStatus.completed ||
          normalizedProgress.processStatus === ProcessStatus.failed)) {
        importWizardStore.handleUploadProgress(normalizedProgress).catch((err: unknown) => {
          console.error('Feil ved håndtering av upload progress:', err);
        });
      }
    });

    conn.on('ReceiveExportProgress', async (progress: ExportTaskStatus) => {
      await tableStore.updateExportProgress(progress);
    });

    // Bug 2970: e-post sendes nå asynkront av workeren; utfallet (Sent/Failed/Partial/
    // Skipped) pushes hit over user_{oid}-gruppen. Speiler ReceiveExportProgress.
    conn.on('ReceiveEmailOutcome', (payload: EmailOutcomeUpdate) => {
      handleEmailOutcome(payload);
    });

    // Analyse progress updates
    conn.on('ReceiveAnalyseProgress', (progressUpdate: AnalyseProgressUpdate) => {
      console.log('ReceiveAnalyseProgress', progressUpdate);
      const event = new CustomEvent('analyseProgress', { detail: progressUpdate });
      globalThis.dispatchEvent(event);
    });

    // Event stream updates
    conn.on('EventStreamUpdate', async (eventStreamUpdate: any) => {
      // Normaliser processType og processStatus til enum-verdier
      const normalizedUpdate: EventStreamUpdate = {
        ...eventStreamUpdate,
        processType: normalizeProcessType(eventStreamUpdate.processType),
        processStatus: normalizeProcessStatus(eventStreamUpdate.processStatus)
      };

      // Oppdater progresses-mappen direkte med EventStreamUpdate
      setProgress(normalizedUpdate);

      // US 3114 task 3118: module provisioning progress (PowerBI/Fabric) is its own channel.
      // Route it to projectModulesStore and skip the import/upload routing below.
      if (normalizedUpdate.processType === ProcessType.setup) {
        const { useProjectModulesStore } = await import('@/store/projectModulesStore');
        useProjectModulesStore().handleProvisioningEvent(normalizedUpdate);
        return;
      }

      if (normalizedUpdate.isComplete && normalizedUpdate.fileId) {
        const { useImportLogStore } = await import('@/store/importLogStore');
        useImportLogStore().invalidateImportLogCache(normalizedUpdate.fileId);
        globalThis.dispatchEvent(new CustomEvent('ics-import-report-complete', { detail: { fileId: normalizedUpdate.fileId } }));
      }

      // Håndter i importWizardStore (for wizard-flyt)
      await importWizardStore.handleEventStreamUpdate(normalizedUpdate);

      // Håndter i uploadStoreV2 (for fil-liste oppdateringer)
      // Bruker statisk importet uploadStore (definert på linje 200)
      try {
        uploadStore.handleEventStreamUpdate(normalizedUpdate);
      } catch (error) {
        // Ignorer hvis store ikke er tilgjengelig (kan skje hvis komponenten ikke er montert)
        console.debug('Could not update uploadStoreV2 from EventStreamUpdate:', error);
      }
    });
  };

  async function initializeConnection (database = ''): Promise<void> {
    console.log('[SignalR] initializeConnection kalt med scope:', database || '(bruker-scopet)');

    // Auth-helperne resolves lazily for å unngå statisk import-syklus
    // (auth.ts importerer addError fra notificationsStore for feilrapportering).
    const { userOid, userAzureId } = await import('@/helpers/auth');
    const oid = userOid.value || '';
    const legacyId = userAzureId.value || '';

    if (shouldSkipInitialization(database, oid)) {
      return;
    }

    // Hvis vi allerede har en tilkobling til et annet scope, koble fra først
    if (connection) {
      await connection.stop();
      connection = null;
    }

    // Sett flag for å forhindre dobbel initialisering
    isConnecting = true;

    console.log('Initialiserer SignalR-tilkobling for scope:', database || '(bruker)');

    // SignalR hubs skal alltid være på /hubs/event direkte, ikke /api/hubs/event
    // VITE_API_URL er for REST API-endepunkter, ikke for SignalR hubs
    const hubURL = '/hubs/event';
    console.log('SignalR connection: userOid =', oid, 'legacyUserId =', legacyId);

    // Bug 2970: dbName utelates for bruker-scopet kobling (BE joiner user_{oid} uansett).
    const params = new URLSearchParams();
    if (database) params.set('dbName', database);
    params.set('userId', oid);
    if (legacyId && legacyId !== oid) params.set('legacyUserId', legacyId);
    const connectionUrl = `${hubURL}?${params.toString()}`;

    connection = buildConnection(connectionUrl);

    try {
      await connection.start();
      currentDatabase.value = database;
      console.log('SignalR-tilkobling etablert for scope:', database || '(bruker)');
      isConnecting = false;

      // Feature-storene resolves lazily for å unngå statiske import-sykler:
      // disse storene importerer typer + addError/addSuccess fra notificationsStore,
      // mens notificationsStore kun trenger dem inne i SignalR-handlerne nedenfor.
      const { useImportWizardStore } = await import('@/store/importWizardStore');
      const { useUploadStore } = await import('@/store/storeUpload');
      const { useTableStore } = await import('@/store/storeTable');
      const importWizardStore: ReturnType<typeof useImportWizardStore> = useImportWizardStore();
      const uploadStore: ReturnType<typeof useUploadStore> = useUploadStore();
      const tableStore: ReturnType<typeof useTableStore> = useTableStore();

      // Synkroniser fil-status for aktive uploads når tilkoblingen etableres
      // Dette håndterer tilfeller hvor SignalR-tilkoblingen ble brutt (f.eks. ved refresh)
      importWizardStore.syncFileStatusOnConnection().catch((err: unknown) => {
        console.error('Feil ved synkronisering av fil-status:', err);
      });

      registerEventHandlers(connection, importWizardStore, uploadStore, tableStore);
    } catch (err) {
      // Reset connecting flag ved feil
      isConnecting = false;

      // Håndter "connection stopped during negotiation" - dette kan skje ved rask navigering
      // og er ofte midlertidig. Automatic reconnect vil håndtere dette.
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes('stopped during negotiation') || errorMessage.includes('AbortError')) {
        console.debug('SignalR-tilkobling avbrutt under forhandling (forventet ved rask navigering):', errorMessage);
        // Automatic reconnect vil prøve igjen
        return;
      }
      console.error('Feil ved etablering av SignalR-tilkobling:', err);
      currentDatabase.value = null;
      connection = null;
    }
  }

  // Hjelpefunksjon for å bestemme border-fargen basert på meldingstypen
  const getColor = (severity?: string): string => {
    switch (severity?.toLowerCase()) {
      case 'error':
        return 'rgb(var(--color-danger))';
      case 'warn':
        return 'rgb(var(--color-warning))';
      case 'success':
        return 'rgb(var(--color-success))';
      case 'info':
        return 'rgb(var(--color-info))';
      default:
        return 'rgb(var(--color-accent-default))';
    }
  };

  const getIcon = (severity?: string): string => {
    switch (severity?.toLowerCase()) {
      case 'error':
        return 'octagon-exclamation';
      case 'warn':
        return 'triangle-exclamation';
      case 'success':
        return 'circle-check';
      case 'info':
        return 'circle-info';
      default:
        return 'question-circle';
    }
  };

  // Dedup-vindu mot self-feedende error-loops (router.onError → addError ×N/sek
  // → fane-krasj). Identiske {severity,summary,detail}-meldinger innen
  // DEDUP_WINDOW_MS kollapses til én entry med (×N)-badge.
  const DEDUP_WINDOW_MS = 1000;
  const INACTIVE_TTL_MS = 30000;
  type DedupEntry = { id: number; count: number; firstAt: number; baseSummary: string };
  const dedupWindow = new Map<string, DedupEntry>();

  function pruneInactiveOlderThan (ttlMs: number): void {
    const cutoff = Date.now() - ttlMs;
    notifications.value = notifications.value.filter((n) => {
      if (n.isActive) return true;
      return n.id > cutoff;
    });
  }

  function addEvent (event: NotificationEvent): void {
    const dedupKey = `${event.severity}|${event.summary}|${event.detail ?? ''}`;
    const now = Date.now();
    const existing = dedupWindow.get(dedupKey);
    if (existing && now - existing.firstAt < DEDUP_WINDOW_MS) {
      const target = notifications.value.find((n) => n.id === existing.id);
      if (target?.isActive) {
        existing.count += 1;
        target.summary = `${existing.baseSummary} (×${existing.count})`;
        return;
      }
      dedupWindow.delete(dedupKey);
    }

    console.log('Adding event:', event);
    const id = now;
    const notification: Notification = {
      ...event,
      id: id,
      createdAt: new Date().toISOString(),
      isActive: true,
      color: getColor(event.severity),
      icon: getIcon(event.severity),
    };
    notifications.value.unshift(notification);
    dedupWindow.set(dedupKey, { id, count: 1, firstAt: now, baseSummary: event.summary });

    const activeNotifications = notifications.value.filter(n => n.isActive);
    if (activeNotifications.length > 4) {
      const oldestActive = activeNotifications.at(-1);
      const index = oldestActive ? notifications.value.findIndex(n => n.id === oldestActive.id) : -1;
      if (index !== -1) {
        notifications.value[index].isActive = false;
      }
    }

    pruneInactiveOlderThan(INACTIVE_TTL_MS);

    globalThis.setTimeout(() => {
      console.log('Removing toast after timeout:', notification);
      const index = notifications.value.findIndex(n => n.id === id);
      if (index !== -1) {
        notifications.value[index].isActive = false;
      }
      dedupWindow.delete(dedupKey);
    }, 5000);
  }

  function clearEvents (): void {
    for (const n of notifications.value) {
      n.isActive = false;
    }
  }

  async function sendMessage (message: string): Promise<void> {
    if (connection) {
      await connection.invoke('SendMessage', message);
    }
  }

  function clearNotifications (): void {
    notifications.value = [];
  }

  function addError (summary: string, detail?: string): void {
    addEvent({ severity: 'error', summary, detail });
  }

  function addProblem (problem: Problem): void {
    let detailMessage = problem.detail;

    if (problem.errors) {
      // Håndter både array av strings og objekt med felter
      if (Array.isArray(problem.errors)) {
        // Backend sender List<string> for valideringsfeil
        detailMessage = problem.errors.join('\n');
      } else {
        // Objekt med felter (for andre typer feil)
        detailMessage = Object.entries(problem.errors)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(' | ') : errors}`)
          .join('\n');
      }
    }

    addError(problem.title, detailMessage);
  }

  function addInfo (summary: string, detail?: string): void {
    addEvent({ severity: 'info', summary, detail });
  }

  function addWarn (summary: string, detail?: string): void {
    addEvent({ severity: 'warn', summary, detail });
  }

  function addSuccess (summary: string, detail?: string): void {
    addEvent({ severity: 'success', summary, detail });
  }

  return {
    notifications,
    getProgress,
    addEvent,
    initializeConnection,
    showNotifications,
    toggleNotifications,
    sendMessage,
    clearNotifications,
    addError,
    addInfo,
    addWarn,
    addSuccess,
    addProblem,
    clearEvents,
    currentDatabase,
  };
});
